import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Unlock, Radio, ArrowUpRight } from "lucide-react";

/**
 * Chapter 02 — Reveal section.
 * BEFORE unlockISO: shows a locked teaser card with countdown label.
 * AFTER unlockISO: swaps to the revealed content with highlights.
 * Auto re-checks every 5s so it flips live if the visitor sits on the page.
 */
export default function ChapterReveal({ data, sectionId = "chapter-02", testIdPrefix = "chapter-02" }) {
  const unlockAt = data?.unlockISO ? new Date(data.unlockISO) : null;
  const [unlocked, setUnlocked] = useState(() =>
    unlockAt ? new Date() >= unlockAt : true
  );

  useEffect(() => {
    if (!unlockAt || unlocked) return;
    const id = setInterval(() => {
      if (new Date() >= unlockAt) setUnlocked(true);
    }, 5000);
    return () => clearInterval(id);
  }, [unlockAt, unlocked]);

  const locked = data.locked || {};
  const revealed = data.revealed || {};

  return (
    <section
      id={sectionId}
      data-testid={`${testIdPrefix}-reveal`}
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 overflow-hidden border-y border-white/10"
    >
      {/* backdrop stripes / halo */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #FACC15 0, #FACC15 10px, transparent 10px, transparent 40px)",
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(250,204,21,0.05),transparent_65%)] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto">
        <AnimatePresence mode="wait">
          {!unlocked ? (
            <motion.div
              key="locked"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
              data-testid={`${testIdPrefix}-locked`}
            >
              {/* Left: lock icon + kicker */}
              <div className="lg:col-span-5">
                <div className="flex items-center gap-4 mb-8">
                  <span className="relative flex items-center justify-center w-14 h-14 border border-accent/60 bg-black/60">
                    <Lock size={22} className="text-accent" />
                    <span className="absolute inset-0 border border-accent/40 animate-pulse" />
                  </span>
                  <div className="flex flex-col gap-1">
                    <span className="font-body text-[10px] uppercase tracking-[0.35em] text-accent">
                      {data.eyebrow}
                    </span>
                    <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/50">
                      {locked.kicker}
                    </span>
                  </div>
                </div>
                <div className="hidden lg:block border-t border-white/10 pt-6">
                  <div className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                    Sblocco automatico
                  </div>
                  <div className="font-heading uppercase text-white text-2xl md:text-3xl tracking-[0.08em]">
                    {data.unlockLabel}
                  </div>
                </div>
              </div>

              {/* Right: locked content */}
              <div className="lg:col-span-7">
                <h2
                  className="font-heading uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-[0.9] mb-6"
                >
                  {locked.title}
                </h2>
                <p className="font-body text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mb-8">
                  {locked.body}
                </p>
                <div className="border border-accent/30 bg-black/50 backdrop-blur-md p-5 md:p-6 max-w-2xl relative overflow-hidden">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.06] pointer-events-none"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(-45deg, #FACC15 0, #FACC15 8px, transparent 8px, transparent 24px)",
                    }}
                  />
                  <div className="relative flex items-center gap-3">
                    <Radio size={12} className="text-accent" />
                    <span className="font-body text-[10px] uppercase tracking-[0.3em] text-accent">
                      Trasmissione bloccata
                    </span>
                  </div>
                  <p className="relative mt-3 font-body text-sm text-white/60 leading-relaxed">
                    {locked.hint}
                  </p>
                </div>

                {/* Mobile date */}
                <div className="lg:hidden mt-8 border-t border-white/10 pt-6">
                  <div className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                    Sblocco automatico
                  </div>
                  <div className="font-heading uppercase text-white text-xl tracking-[0.08em]">
                    {data.unlockLabel}
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="revealed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
              data-testid={`${testIdPrefix}-revealed`}
            >
              {/* Left: unlock icon + kicker */}
              <div className="lg:col-span-5">
                <div className="flex items-center gap-4 mb-8">
                  <span className="relative flex items-center justify-center w-14 h-14 border border-accent/60 bg-black/60">
                    <Unlock size={22} className="text-accent" />
                    <span className="absolute inset-0 border border-accent/40 animate-pulse" />
                  </span>
                  <div className="flex flex-col gap-1">
                    <span className="font-body text-[10px] uppercase tracking-[0.35em] text-accent">
                      {data.eyebrow}
                    </span>
                    <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/60 inline-flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                      {revealed.kicker}
                    </span>
                  </div>
                </div>
                <div className="hidden lg:block border-t border-white/10 pt-6">
                  <div className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                    Sbloccato il
                  </div>
                  <div className="font-heading uppercase text-white text-2xl md:text-3xl tracking-[0.08em]">
                    {data.unlockLabel}
                  </div>
                </div>
              </div>

              {/* Right: revealed content */}
              <div className="lg:col-span-7">
                <h2 className="font-heading uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-[0.9] mb-6">
                  {revealed.title}
                </h2>
                <p className="font-body text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mb-10">
                  {revealed.body}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {(revealed.highlights || []).map((h, i) => (
                    <motion.div
                      key={h.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.15 + i * 0.1, duration: 0.6 }}
                      className="border border-white/10 bg-black/40 backdrop-blur-sm p-5 hover:border-accent/40 transition-colors group"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-body text-[10px] uppercase tracking-[0.3em] text-accent/70">
                          {h.id}
                        </span>
                        <ArrowUpRight
                          size={14}
                          className="text-white/30 group-hover:text-accent transition-colors"
                        />
                      </div>
                      <div className="font-heading uppercase text-white text-lg leading-tight mb-2">
                        {h.title}
                      </div>
                      <p className="font-body text-xs text-white/55 leading-relaxed">
                        {h.body}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {revealed.footnote && (
                  <p className="font-body text-[11px] uppercase tracking-[0.25em] text-white/40 leading-relaxed max-w-2xl">
                    {revealed.footnote}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
